import { DateValue, GregorianCalendar } from "@internationalized/date";
import * as React from "react";
import {
  AriaDatePickerProps,
  useDateField,
  useDateSegment,
  useLocale,
} from "react-aria";
import { Flex } from "../flex";
import { DateFieldState, useDateFieldState } from "react-stately";
import { DateSegment as DateSegmentType } from "react-stately";
import { Text } from "../text";
import { mergeRefs } from "../../utils/dom";

interface DateFieldProps extends AriaDatePickerProps<DateValue> {
  error?: string;
}

function createCalendar() {
  return new GregorianCalendar();
}

export const DateField = React.forwardRef<HTMLDivElement, DateFieldProps>(
  (props, ref) => {
    const { locale } = useLocale();

    const state = useDateFieldState({
      ...props,
      locale,
      createCalendar,
    });

    const fieldRef = React.useRef(null);
    const { fieldProps } = useDateField(props, state, fieldRef);

    return (
      <Flex
        gap="none"
        style={{ padding: "4px 0px", flex: 1 }}
        stretchY
        ref={mergeRefs(fieldRef, ref)}
        inline
        {...fieldProps}
      >
        {state.segments.map((segment, i) => {
          return (
            <DateSegment key={i} segment={segment} state={state}></DateSegment>
          );
        })}
      </Flex>
    );
  }
);

function DateSegment({
  segment,
  state,
}: {
  segment: DateSegmentType;
  state: DateFieldState;
}) {
  const ref = React.useRef(null);
  const { segmentProps } = useDateSegment(segment, state, ref);

  const segmentMaxValue = segment.maxValue ?? 0;

  return (
    <Flex
      className="p-date-input-segment"
      align={"center"}
      justify={"center"}
      ref={ref}
      data-placeholder={segment.isPlaceholder}
      data-separator={segment.text === "/"}
      style={{
        minWidth: segmentMaxValue.toString().length + "ch",
        color: segment.text == "/" ? "var(--color-6)" : "var(--color-12)",
      }}
      {...segmentProps}
    >
      <Text
        size={"sm"}
        variant={segment.isPlaceholder ? "dark" : "lighter"}
        as="span"
        aria-hidden="true"
        style={{
          color: "inherit",
          display: "block",
          height: segment.isPlaceholder ? "" : 0,
          pointerEvents: "none",
          visibility: segment.isPlaceholder ? undefined : "hidden",
          position: segment.isPlaceholder ? undefined : "absolute",
        }}
      >
        {segment.placeholder}
      </Text>
      {segment.isPlaceholder ? "" : segment.text}
    </Flex>
  );
}

DateField.displayName = "DateField";
